import { addDays } from "date-fns";

import { FINANCE_DUE_SOON_DAYS } from "../providers/commons/financeAlerts";
import { toDateOnly, toMonth } from "../providers/commons/financeSchedule";

/** Quick filters of the receivables list, kept in the `view` URL param */
export const CHARGE_VIEWS = [
  "open",
  "overdue",
  "due_soon",
  "not_sent",
  "no_invoice",
  "this_month",
  "paid",
  "all",
] as const;

export type ChargeView = (typeof CHARGE_VIEWS)[number];

/** Quick filters of the payables list */
export const EXPENSE_VIEWS = [
  "open",
  "due_soon",
  "this_month",
  "paid",
  "all",
] as const;

export type ExpenseView = (typeof EXPENSE_VIEWS)[number];

const UNPAID = { "paid_at@is": null, "cancelled_at@is": null };

/** Server filter (postgrest syntax) of a receivables quick filter */
export const getChargeViewFilter = (
  view: ChargeView,
  today: Date,
): Record<string, unknown> => {
  const todayDate = toDateOnly(today);
  switch (view) {
    case "open":
      return UNPAID;
    case "overdue":
      return { ...UNPAID, "due_date@lt": todayDate };
    case "due_soon":
      return {
        ...UNPAID,
        "due_date@gte": todayDate,
        "due_date@lte": toDateOnly(addDays(today, FINANCE_DUE_SOON_DAYS)),
      };
    case "not_sent":
      return {
        ...UNPAID,
        "sent_at@is": null,
        "due_date@gte": todayDate,
        "competence_month@lte": toMonth(today),
      };
    case "no_invoice":
      return {
        "paid_at@not.is": null,
        "cancelled_at@is": null,
        "invoice_number@is": null,
      };
    case "this_month":
      return { competence_month: toMonth(today) };
    case "paid":
      return { "paid_at@not.is": null };
    case "all":
      return {};
  }
};

/** Server filter of a payables quick filter */
export const getExpenseViewFilter = (
  view: ExpenseView,
  today: Date,
): Record<string, unknown> => {
  switch (view) {
    case "open":
      return { "paid_at@is": null };
    case "due_soon":
      return {
        "paid_at@is": null,
        "due_date@lte": toDateOnly(addDays(today, FINANCE_DUE_SOON_DAYS)),
      };
    case "this_month":
      return { competence_month: toMonth(today) };
    case "paid":
      return { "paid_at@not.is": null };
    case "all":
      return {};
  }
};

/** Reads a quick filter from the URL, falling back to the default one */
export const parseView = <T extends string>(
  value: string | null,
  views: readonly T[],
): T => (views.includes(value as T) ? (value as T) : views[0]);
